import { Box, ResponsiveContext, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import React, { useContext } from 'react';
import { useState } from 'react';
import { ThickLeftBorderContainer } from '../../../utils/styled';
import AddressDrawer from '../../AddressDrawer';
import CustomerLabel from '../../CustomerLabel';
import CustomerTag from '../../CustomerTag';
import DataTable from '../../DataTable';
import InvoiceTag from '../../InvoiceTag';
import { NotificationsContext } from '../../NotificationsProvider';
import {
    IconAlertCircle as IconExclamation
} from '@tabler/icons-react';
import FontAwesomeIcon from '../../FontAwesomeIcon';
import { handleErrors } from '../../../utils/errors';
import { useSWRConfig } from 'swr';

const BouncedEmailTable = ({bouncedEmailData, onUpdate}) => {
    const { mutate } = useSWRConfig();
    const [showAddressEditDrawer, setShowAddressEditDrawer] = useState(false);
    const [customerToEdit, setCustomerToEdit] = useState();
    const theme = useContext(ThemeContext);
    const borderColor = normalizeColor(theme.global.colors["border"], theme);
    const color = "status-warning";
    const accentColor = normalizeColor(theme.global.colors[color], theme);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const notificationCtx = useContext(NotificationsContext);

    const saveDetails = ({ value }) => {
        const formData = {...value, update_email: true};
        fetch(`/api/customers/${customerToEdit.customer_id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                addresses_attributes: [formData],
            }),
        }).then(handleErrors)
            .then(() => {
                mutate(`/api/company/bounced_emails`);
                notificationCtx.showNotification({message: 'Changes saved'});
                if (onUpdate) onUpdate();
            })
            .catch(notificationCtx.showNotification);
    };

    return !bouncedEmailData || !bouncedEmailData?.bounced_emails.length ?
        (<></>) :
        (
            <ThickLeftBorderContainer
                pad={{ horizontal: "medium", top: 'medium', bottom: 'small' }}
                margin={{ top: "small", bottom: "medium" }}
                round={{ corner: 'left', size: 'xsmall' }}
                borderColor={borderColor}
                accentColor={accentColor}
                elevation="medium"
            >
                <Box direction="row" gap="small" align={isMobile ? "start" : "center"} fill="horizontal">
                    <FontAwesomeIcon
                        color="status-warning"
                        SelectedIcon={IconExclamation}
                        iconContainerProps={{
                            width: isMobile ? 'xxsmall' : 'auto',
                            margin: {right: isMobile ? 'small' : ''}
                        }}
                    />
                    <Box>
                        <Text weight={500}>
                            We couldn’t deliver invoices to these customers
                        </Text>
                        <Text size="small" weight={400}>
                            Click on a row below to update the customer’s email address. Once you’re done, any undelivered invoices will be re-sent automatically.
                        </Text>
                    </Box>
                </Box>
                <DataTable
                    dataName='bounced_emails'
                    data={bouncedEmailData?.bounced_emails}
                    columns={isMobile ? [
                        {
                            selector: row => row.customer_id,
                            cell: (datum) => {
                                return (
                                    <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                                        <Box data-tag="allowRowEvents">
                                            <CustomerLabel data-tag="allowRowEvents" customerId={datum?.customer_id} />
                                            <Text data-tag="allowRowEvents" size="small" color="text-weak">{datum?.email}</Text>
                                        </Box>
                                    </Box>
                                )
                            }
                        }
                    ] : [
                        {
                            selector: (row) => row.customer_id,
                            name: "Customer",
                            cell: (datum) => datum?.customer_id && <CustomerTag uuid={datum.customer_uuid} inline />,
                        },
                        {
                            selector: (row) => row.email,
                            name: "Bounced email",
                            cell: (datum) => datum?.email
                        },
                        {
                            selector: (row) => row.affected_invoices,
                            name: "Affected invoices",
                            grow: 2,
                            cell: (datum) => datum?.affected_invoices && (
                                <Box direction="row" gap="xsmall">
                                    {datum?.affected_invoices.map(id => <InvoiceTag id={id} key={id} inline />)}
                                </Box>
                            )
                        },
                    ]}
                    hideExport={true}
                    noTableHead={isMobile}
                    onRowClicked={(row) => {
                        setCustomerToEdit(row);
                        setShowAddressEditDrawer(true);
                    }}
                    dataTableWrapperProps={{hideDeepLink: true, margin: 'none'}}
                />
                {showAddressEditDrawer && (
                    <AddressDrawer
                        title={'Update email'}
                        onSave={saveDetails}
                        onClose={() => setShowAddressEditDrawer(false)}
                        addressId={customerToEdit?.address_id}
                        updateEmail
                    />
                )}
            </ThickLeftBorderContainer>
        );
}

export default BouncedEmailTable;